import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { FaChevronLeft, FaChevronRight, FaChurch, FaCheckCircle, FaQuoteLeft } from 'react-icons/fa';

/** Si hay más avales que este umbral, se usa carrusel (p. ej. 4+ con umbral 3). */
export const MICROSITE_AVALES_CAROUSEL_THRESHOLD = 3;

function normalizeVariant(variant) {
  if (variant === 'clasico') return 'clasico';
  if (variant === 'elegante') return 'elegante';
  if (variant === 'aurora') return 'aurora';
  return 'moderno';
}

function getCardConfig(normalized, surfaceColor) {
  switch (normalized) {
    case 'clasico':
      return {
        boxProps: {
          p: 5,
          rounded: 'lg',
          border: '1px solid',
          borderColor: 'gray.200',
          bg: surfaceColor,
          shadow: 'none',
          position: 'relative',
        },
        quoteProps: { position: 'absolute', top: 3, left: 3, color: 'gray.200', boxSize: 6 },
        bodyTextProps: { fontSize: 'sm', color: 'gray.600', fontStyle: 'italic', lineHeight: '1.7' },
        nameTextProps: { fontSize: 'xs', fontWeight: '700', color: 'gray.700' },
        subTextProps: { fontSize: '10px', color: 'gray.400' },
        checkSize: 3,
        avatarSize: 'xs',
        stackPt: 3,
        stackPl: 2,
        prefixAval: true,
      };
    case 'elegante':
      return {
        boxProps: {
          position: 'relative',
          py: 6,
          px: 4,
          bg: 'transparent',
          shadow: 'none',
          border: 'none',
        },
        quoteProps: { position: 'absolute', top: 0, left: 0, color: 'gray.100', boxSize: 10 },
        bodyTextProps: {
          fontSize: 'md',
          color: 'gray.600',
          fontStyle: 'italic',
          lineHeight: '1.8',
          fontWeight: '300',
        },
        nameTextProps: { fontSize: 'sm', fontWeight: '500', color: 'gray.700' },
        subTextProps: { fontSize: 'xs', color: 'gray.400', fontWeight: '300' },
        checkSize: 3,
        avatarSize: 'sm',
        stackPt: 4,
        stackPl: 4,
        prefixAval: false,
      };
    case 'aurora':
      return {
        boxProps: {
          bg: 'gray.800',
          p: 6,
          rounded: 'xl',
          shadow: 'sm',
          position: 'relative',
        },
        quoteProps: { position: 'absolute', top: 4, left: 4, color: 'whiteAlpha.100', boxSize: 8 },
        bodyTextProps: { fontSize: 'sm', color: 'gray.400', fontStyle: 'italic', lineHeight: '1.7' },
        nameTextProps: { fontSize: 'sm', fontWeight: '700', color: 'gray.100' },
        subTextProps: { fontSize: 'xs', color: 'gray.400' },
        checkSize: 3.5,
        avatarSize: 'sm',
        stackPt: 4,
        stackPl: 2,
        prefixAval: true,
      };
    default:
      return {
        boxProps: {
          bg: 'white',
          p: 6,
          rounded: 'xl',
          shadow: 'sm',
          position: 'relative',
        },
        quoteProps: { position: 'absolute', top: 4, left: 4, color: 'purple.100', boxSize: 8 },
        bodyTextProps: { fontSize: 'sm', color: 'gray.600', fontStyle: 'italic', lineHeight: '1.7' },
        nameTextProps: { fontSize: 'sm', fontWeight: '700', color: 'gray.800' },
        subTextProps: { fontSize: 'xs', color: 'gray.400' },
        checkSize: 3.5,
        avatarSize: 'sm',
        stackPt: 4,
        stackPl: 2,
        prefixAval: true,
      };
  }
}

function AvalCard({ aval, cfg }) {
  const dateStr = aval.acceptedAt
    ? new Date(aval.acceptedAt).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
    : '';
  const subLabel = cfg.prefixAval ? `Aval desde ${dateStr}` : dateStr;

  return (
    <Box {...cfg.boxProps} h="full">
      <Icon {...cfg.quoteProps} asChild><FaQuoteLeft /></Icon>
      <VStack align="start" gap={3} pt={cfg.stackPt} pl={cfg.stackPl}>
        {aval.texto ? (
          <Text {...cfg.bodyTextProps}>&ldquo;{aval.texto}&rdquo;</Text>
        ) : null}
        <HStack gap={cfg.avatarSize === 'xs' ? 2 : 3}>
          <Avatar.Root
            size={cfg.avatarSize}
            bg={cfg.normalized === 'elegante' ? 'gray.100' : 'green.100'}
            color={cfg.normalized === 'elegante' ? undefined : 'green.600'}><Avatar.Fallback name={aval.cofradia?.nombre}>{<Icon color={cfg.normalized === 'elegante' ? 'gray.400' : undefined} asChild><FaChurch /></Icon>}</Avatar.Fallback><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
          <VStack align="start" gap={0}>
            <HStack gap={cfg.normalized === 'elegante' ? 1 : undefined}>
              <Text {...cfg.nameTextProps}>{aval.cofradia?.nombre}</Text>
              <Icon
                color={cfg.normalized === 'elegante' ? 'green.400' : 'green.500'}
                w={cfg.checkSize}
                h={cfg.checkSize}
                asChild><FaCheckCircle /></Icon>
            </HStack>
            {dateStr ? <Text {...cfg.subTextProps}>{subLabel}</Text> : null}
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
}

/**
 * Avales centrados; si hay más de `MICROSITE_AVALES_CAROUSEL_THRESHOLD`, carrusel con snap y flechas (desktop).
 */
export default function MicrositeAvalesBlock({
  avales,
  variant = 'moderno',
  surfaceColor = 'gray.50',
  mb = 0,
}) {
  const list = Array.isArray(avales) ? avales : [];
  const normalized = normalizeVariant(variant);
  const cfgBase = getCardConfig(normalized, surfaceColor);
  const cfg = { ...cfgBase, normalized };

  const scrollRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const useCarousel = list.length > MICROSITE_AVALES_CAROUSEL_THRESHOLD;

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !useCarousel) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(max > 4 && el.scrollLeft < max - 4);
  }, [useCarousel]);

  useLayoutEffect(() => {
    updateArrows();
  }, [list.length, useCarousel, updateArrows]);

  useEffect(() => {
    if (!useCarousel) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);
    window.addEventListener('resize', updateArrows);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateArrows);
    };
  }, [useCarousel, updateArrows]);

  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.min(el.clientWidth * 0.75, 360) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (list.length === 0) return null;

  if (!useCarousel) {
    return (
      <Flex justify="center" align="stretch" flexWrap="wrap" gap={{ base: 4, md: 5 }} mb={mb} w="full">
        {list.map((aval) => (
          <Box
            key={aval.id}
            flex={{ base: '1 1 100%', md: '0 1 calc(50% - 12px)' }}
            maxW={{ base: '100%', md: '420px' }}
            minW={{ md: '260px' }}
          >
            <AvalCard aval={aval} cfg={cfg} />
          </Box>
        ))}
      </Flex>
    );
  }

  return (
    <Box mb={mb} w="full" maxW="100%">
      <Flex align="center" gap={{ base: 0, md: 2 }} w="full" justify="center">
        <IconButton
          display={{ base: 'none', md: 'flex' }}
          aria-label="Aval anterior"
          variant="ghost"
          size="md"
          flexShrink={0}
          onClick={() => scrollByDir(-1)}
          disabled={!canPrev}
          colorPalette="purple"><FaChevronLeft /></IconButton>
        <Box
          ref={scrollRef}
          onScroll={updateArrows}
          overflowX="auto"
          overflowY="hidden"
          flex="1"
          maxW={{ base: '100%', md: 'calc(100% - 88px)' }}
          py={1}
          px={{ base: 2, md: 1 }}
          css={{
            scrollSnapType: 'x mandatory',
            '& scrollPaddingLeft': { base: '16px', md: '24px' },
            '& scrollPaddingRight': { base: '16px', md: '24px' },
            WebkitOverflowScrolling: 'touch',
            '& &::-webkit-scrollbar': { height: '6px' },

            '& &::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-purple-200)',
              borderRadius: 'full',
            }
          }}
        >
          <HStack
            gap={5}
            align="stretch"
            w="max-content"
            mx="auto"
            px={{ base: 1, md: 2 }}
          >
            {list.map((aval) => (
              <Box
                key={aval.id}
                flex="0 0 auto"
                w={{ base: 'min(88vw, 360px)', md: '340px' }}
                maxW="400px"
                scrollSnapAlign="center"
              >
                <AvalCard aval={aval} cfg={cfg} />
              </Box>
            ))}
          </HStack>
        </Box>
        <IconButton
          display={{ base: 'none', md: 'flex' }}
          aria-label="Aval siguiente"
          variant="ghost"
          size="md"
          flexShrink={0}
          onClick={() => scrollByDir(1)}
          disabled={!canNext}
          colorPalette="purple"><FaChevronRight /></IconButton>
      </Flex>
    </Box>
  );
}
